import { create } from 'zustand';
import { http } from '@/libs/http';
import type { Role, RoleCreate, RoleUpdate } from '@/types';
import { toast } from 'sonner';

const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    return 'Unknown error';
};

interface RoleStats {
    role_id: string;
    role_name: string;
    permission_count: number;
    direct_user_count: number;
    group_assignment_count: number;
}

interface RoleValidation {
    role_id: string;
    is_valid: boolean;
    errors: string[];
    warnings: string[];
}

export interface RoleUserAssignment {
    user_id: string;
    name?: string;
    email: string;
    assignment_source: 'direct' | 'group';
    via_group_id?: string | null;
    via_group_name?: string | null;
    scope_id: string;
    expires_at?: string | null;
}

interface RoleState {
    roles: Role[];
    isLoading: boolean;
    error: string | null;
    
    // Core actions
    list: (filter?: (roles: Role[]) => Role[]) => Promise<Role[]>;
    listWithFilters: (scope?: string, isCustom?: boolean, deprecated?: boolean) => Promise<Role[]>;
    get: (id: string) => Promise<Role>;
    create: (data: RoleCreate) => Promise<void>;
    update: (id: string, data: RoleUpdate) => Promise<void>;
    delete: (id: string) => Promise<void>;
    select: (criteria: (roles: Role[]) => Role) => Role;

    // Permissions
    grantPermission: (roleId: string, permId: string) => Promise<void>;
    revokePermission: (roleId: string, permId: string) => Promise<void>;
    listPermissions: (roleId: string) => Promise<string[]>;
    
    // Validation & Stats
    validateRole: (roleId: string) => Promise<RoleValidation>;
    getStats: (roleId: string) => Promise<RoleStats>;
    getUserCount: (roleId: string) => Promise<number>;
    listUsers: (roleId: string) => Promise<RoleUserAssignment[]>;
}

export const useRoles = create<RoleState>((set, get) => ({
    roles: [],
    isLoading: false,
    error: null,

    list: async (filter) => {
        set({ isLoading: true, error: null });
        try {
            const response = await http.get<{ items: Role[] }>('/mgmt/v1/roles');
            const data = response.items || [];
            set({ roles: data, isLoading: false });
            return filter ? filter(data) : data;
        } catch (err: unknown) {
            set({ error: getErrorMessage(err), isLoading: false });
            toast.error('Failed to load roles');
            return [];
        }
    },
    
    listWithFilters: async (scope, isCustom, deprecated) => {
        set({ isLoading: true, error: null });
        try {
            const params = new URLSearchParams();
            if (scope) params.append('scope', scope);
            if (isCustom !== undefined) params.append('is_custom', String(isCustom));
            if (deprecated !== undefined) params.append('deprecated', String(deprecated));
            
            const url = `/mgmt/v1/roles${params.toString() ? `?${params.toString()}` : ''}`;
            const response = await http.get<{ items: Role[] }>(url);
            const data = response.items || [];
            set({ roles: data, isLoading: false });
            return data;
        } catch (err: unknown) {
            set({ error: getErrorMessage(err), isLoading: false });
            toast.error('Failed to load roles');
            return [];
        }
    },

    get: async (id) => {
        const found = get().roles.find((role) => role.id === id);
        if (found) return found;
        const data = await http.get<Role>(`/mgmt/v1/roles/${id}`);
        set({ roles: [...get().roles.filter((r) => r.id !== id), data] });
        return data;
    },

    create: async (data) => {
        const previousRoles = get().roles;
        const tempId = `temp-${Date.now()}`;
        const tempRole = { ...data, id: tempId } as unknown as Role;
        
        set({ roles: [...previousRoles, tempRole] });

        try {
            const newRole = await http.post<Role>('/mgmt/v1/roles', data);
            set({ 
                roles: get().roles.map(r => r.id === tempId ? newRole : r) 
            });
        } catch (err: unknown) {
            set({ roles: previousRoles, error: getErrorMessage(err) });
            throw err;
        }
    },

    update: async (id, data) => {
        const previousRoles = get().roles;
        set({
            roles: previousRoles.map(r => r.id === id ? { ...r, ...data } : r) as Role[]
        });

        try {
            const updatedRole = await http.put<Role>(`/mgmt/v1/roles/${id}`, data);
            set({
                roles: get().roles.map(r => r.id === id ? updatedRole : r)
            });
        } catch (err: unknown) {
            set({ roles: previousRoles, error: getErrorMessage(err) });
            throw err;
        }
    },

    delete: async (id) => {
        const previousRoles = get().roles;
        set({
            roles: previousRoles.filter(r => r.id !== id)
        });

        try {
            await http.delete(`/mgmt/v1/roles/${id}`);
        } catch (err: unknown) {
            set({ roles: previousRoles, error: getErrorMessage(err) });
            throw err;
        }
    },

    select: (criteria) => {
        return criteria(get().roles);
    },

    grantPermission: async (roleId, permId) => {
        await http.post(`/mgmt/v1/roles/${roleId}/permissions/${permId}`, {});
    },

    revokePermission: async (roleId, permId) => {
        await http.delete(`/mgmt/v1/roles/${roleId}/permissions/${permId}`);
    },

    listPermissions: async (roleId) => {
        return http.get(`/mgmt/v1/roles/${roleId}/permissions`);
    },
    
    validateRole: async (roleId) => {
        try {
            return await http.post<RoleValidation>(`/mgmt/v1/roles/${roleId}/validate`, {});
        } catch (err: unknown) {
            toast.error('Failed to validate role');
            throw err;
        }
    },
    
    getStats: async (roleId) => {
        try {
            return await http.get<RoleStats>(`/mgmt/v1/roles/${roleId}/stats`);
        } catch (err: unknown) {
            toast.error('Failed to load role statistics');
            throw err;
        }
    },
    
    getUserCount: async (roleId) => {
        try {
            const response = await http.get<{ user_count: number }>(`/mgmt/v1/roles/${roleId}/users`);
            return response.user_count;
        } catch (err: unknown) {
            void err;
            return 0;
        }
    },

    listUsers: async (roleId) => {
        try {
            const response = await http.get<{ users: RoleUserAssignment[] }>(`/mgmt/v1/roles/${roleId}/users`);
            return Array.isArray(response.users) ? response.users : [];
        } catch (err: unknown) {
            toast.error('Failed to load role users');
            throw err;
        }
    },
}));

export const useRole = (id?: string) => {
    const role = useRoles((state) =>
        id ? state.roles.find((item) => item.id === id) ?? null : null,
    );

    const ensureId = (): string => {
        if (!id) throw new Error('Role id is required.');
        return id;
    };

    return {
        role,
        get: () => useRoles.getState().get(ensureId()),
        update: (data: RoleUpdate) => useRoles.getState().update(ensureId(), data),
        delete: () => useRoles.getState().delete(ensureId()),
        permissions: {
            list: () => useRoles.getState().listPermissions(ensureId()),
            create: (permId: string) => useRoles.getState().grantPermission(ensureId(), permId),
            delete: (permId: string) => useRoles.getState().revokePermission(ensureId(), permId),
        },
    };
};
